/**
 * cubitplayShareCard
 * Generates a 1080×1920 (9:16) Instagram Stories card for the
 * "Daily Double — Both Done" celebration.
 *
 * Returns { blob: Blob, filename: string }.
 */

const C = {
  bg:      '#0c1510',
  ink:     '#f0f0e8',
  muted:   '#6a8a7a',
  border:  'rgba(255,255,255,0.08)',
  ps:      '#FF8852',
  psDark:  '#E56F3F',
  dc:      '#14B8A6',
  dcDark:  '#0F766E',
};

function streakDisplayLabel(n) {
  if (typeof n !== 'number' || n < 1) return 'TODAY ✓';
  return `${n}-DAY STREAK`;
}

function addGlow(ctx, cx, cy, r, hex, W, H) {
  const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
  g.addColorStop(0, hex + '2e');
  g.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
}

function hline(ctx, y, W, pad) {
  ctx.save();
  ctx.strokeStyle = C.border;
  ctx.lineWidth   = 2;
  ctx.beginPath();
  ctx.moveTo(pad, y);
  ctx.lineTo(W - pad, y);
  ctx.stroke();
  ctx.restore();
}

function drawCrosshair(ctx, cx, cy, size, color, lw = 4) {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth   = lw;
  ctx.lineCap     = 'round';
  ctx.beginPath();
  ctx.arc(cx, cy, size, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(cx - size * 1.55, cy);
  ctx.lineTo(cx + size * 1.55, cy);
  ctx.moveTo(cx, cy - size * 1.55);
  ctx.lineTo(cx, cy + size * 1.55);
  ctx.stroke();
  ctx.restore();
}

function drawScissors(ctx, cx, cy, size, color, lw = 4) {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth   = lw;
  ctx.lineCap     = 'round';
  ctx.beginPath();
  ctx.moveTo(cx - size, cy - size * 0.6);
  ctx.lineTo(cx + size, cy + size * 0.6);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(cx + size, cy - size * 0.6);
  ctx.lineTo(cx - size, cy + size * 0.6);
  ctx.stroke();
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(cx, cy, lw * 1.4, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

export async function generateBothDoneCard({ psStreak = null, dcStreak = null } = {}) {
  // Wait for web fonts (Space Grotesk / Figtree)
  await Promise.race([
    document.fonts?.ready ?? Promise.resolve(),
    new Promise(r => setTimeout(r, 1500)),
  ]);

  const W = 1080, H = 1920;
  const canvas = document.createElement('canvas');
  canvas.width  = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D not available in this browser.');

  const CX  = W / 2;
  const PAD = 100;

  /* ── Background ────────────────────────────────────────────── */
  ctx.fillStyle = C.bg;
  ctx.fillRect(0, 0, W, H);

  /* ── Radial glow orbs for depth ────────────────────────────── */
  addGlow(ctx, 240, 660,  360, C.ps, W, H);
  addGlow(ctx, 840, 1300, 360, C.dc, W, H);

  /* ── Subtle noise texture ───────────────────────────────────── */
  ctx.save();
  ctx.globalAlpha = 0.018;
  for (let i = 0; i < W * H * 0.0008; i++) {
    ctx.fillStyle = '#fff';
    ctx.fillRect(Math.random() * W, Math.random() * H, 1, 1);
  }
  ctx.restore();

  /* ── Split top bar ──────────────────────────────────────────── */
  ctx.fillStyle = C.ps;
  ctx.fillRect(0, 0, W / 2, 10);
  ctx.fillStyle = C.dc;
  ctx.fillRect(W / 2, 0, W / 2, 10);

  /* ── CubitPlay wordmark ─────────────────────────────────────── */
  ctx.textAlign    = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.font         = '800 72px "Space Grotesk", Arial, sans-serif';
  ctx.fillStyle    = C.ink;
  ctx.fillText('cubitplay.', CX, 210);

  /* ── Eyebrow ────────────────────────────────────────────────── */
  ctx.font      = '600 24px Figtree, Arial, sans-serif';
  ctx.fillStyle = C.muted;
  ctx.fillText('BOTH DONE TODAY', CX, 278);

  hline(ctx, 318, W, PAD);

  /* ── Game section helper ────────────────────────────────────── */
  const drawSection = (isPs, streak, topY) => {
    const accent  = isPs ? C.ps    : C.dc;
    const dark    = isPs ? C.psDark : C.dcDark;
    const label   = isPs ? 'PERFECT SLICE' : 'DEAD CENTER';

    // Icon + game label
    const iconCX = PAD + 40;
    const iconCY = topY + 90;
    if (isPs) drawScissors(ctx, iconCX, iconCY, 26, accent);
    else      drawCrosshair(ctx, iconCX, iconCY, 24, accent);

    ctx.textAlign = 'left';
    ctx.font      = '700 32px Figtree, Arial, sans-serif';
    ctx.fillStyle = dark;
    ctx.fillText(label, PAD + 90, iconCY + 12);

    // Big streak value
    const sl      = streakDisplayLabel(streak);
    const isToday = sl === 'TODAY ✓';
    const bigPx   = isToday ? 96 : (streak >= 10 ? 108 : 144);
    ctx.font      = `800 ${bigPx}px "Space Grotesk", Arial, sans-serif`;
    ctx.fillStyle = accent;
    ctx.textAlign = 'center';
    ctx.fillText(sl, CX, topY + 280);

    // Flame / trophy decoration for milestone streaks
    if (typeof streak === 'number' && streak >= 7) {
      ctx.font = '80px serif';
      ctx.fillText(streak >= 30 ? '\uD83C\uDFC6' : '\uD83D\uDD25', CX, topY + 380);
    }
  };

  // Perfect Slice section (Y 340 – 760)
  drawSection(true,  psStreak, 340);

  hline(ctx, 780, W, PAD);

  // Dead Center section (Y 800 – 1180)
  drawSection(false, dcStreak, 800);

  /* ── Footer ─────────────────────────────────────────────────── */
  hline(ctx, 1220, W, PAD);

  const today = new Date().toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  }).toUpperCase();

  ctx.textAlign = 'center';
  ctx.font      = '500 28px Figtree, Arial, sans-serif';
  ctx.fillStyle = C.muted;
  ctx.fillText(today, CX, 1300);

  ctx.font      = '800 50px "Space Grotesk", Arial, sans-serif';
  ctx.fillStyle = C.ink;
  ctx.fillText('cubitplay.com', CX, 1390);

  ctx.font      = '500 24px Figtree, Arial, sans-serif';
  ctx.fillStyle = C.muted;
  ctx.fillText('TINY GAMES. SHARP INSTINCTS.', CX, 1450);

  /* ── Export as blob ─────────────────────────────────────────── */
  const dateStr = new Date().toISOString().slice(0, 10);
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      b => b
        ? resolve({ blob: b, filename: `cubitplay-both-done-${dateStr}.png` })
        : reject(new Error('Image export failed — toBlob returned null.')),
      'image/png',
    )
  );
}
